import React, {useEffect, useRef} from 'react';
import {MapContainer, Popup, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Constants from '../../../utils/constants';
import CustomTileLayer from '../../../app/components/layout/map/CustomTileLayer.js';
import FullscreenControl from '../../../app/components/map/FullscreenControl.js';
import CartePavCanvasLayer from './CartePavCanvasLayer.js';
import CarteCommunesLayer from './CarteCommunesLayer.js';
import CarteCircuitsLayer from './CarteCircuitsLayer.js';
import {trouverCircuit} from '../../selectors/circuits.js';

/*
 * Seul point d'entrée du moteur cartographique (Leaflet / react-leaflet) pour l'écran Carte : le reste de l'écran
 * (filtres, panneau, popup, calques, curseur) n'importe ni `leaflet` ni `react-leaflet`. Changer de moteur plus
 * tard = réécrire ce fichier et ses couches (CartePavCanvasLayer, CarteCommunesLayer).
 *
 * Fond aérien : orthophotographie IGN, Géoplateforme (WMTS, projection Web Mercator « PM », JPEG).
 *   https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal
 *   &TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=image/jpeg
 * Attribution : © IGN - Géoplateforme (Licence Ouverte Etalab 2.0).
 */
export const URL_FOND_AERIEN = 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=image/jpeg';
const ATTRIBUTION_IGN = '© IGN - Géoplateforme';

// Position des contrôles natifs (plein écran) sous le bloc zoom ± de la maquette (haut droite).
const STYLE_CARTE = `
.carte-pilotage .leaflet-top.leaflet-right { top: 78px; right: 6px; }
.carte-pilotage .leaflet-bar { border: 1px solid #D5DAE1; border-radius: 999px; box-shadow: 0 1px 2px rgba(16,46,105,.06); }
.carte-pilotage .leaflet-bar a.fullscreen-button { width: 36px; height: 36px; line-height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 999px; border-bottom: 0; color: #2D333B; }
.carte-pilotage .leaflet-container { font-family: "Open Sans", sans-serif; }
.carte-pilotage .carte-circuit-libelle { background: transparent; border: 0; box-shadow: none; padding: 0; font-size: 11px; font-weight: 700; color: #213067; text-shadow: 0 0 3px #FFFFFF, 0 0 3px #FFFFFF, 0 0 2px #FFFFFF; pointer-events: none; }
.carte-pilotage .carte-circuit-libelle:before { display: none; }
`;

function FondAerien() {
    const map = useMap();
    useEffect(() => {
        const couche = L.tileLayer(URL_FOND_AERIEN, {maxZoom: 19, maxNativeZoom: 19, attribution: ATTRIBUTION_IGN});
        couche.addTo(map);
        return () => {
            map.removeLayer(couche);
        };
    }, [map]);
    return null;
}

/** Recalcule la taille de Leaflet quand son conteneur change (panneau replié, plein écran). */
function SuiviTaille() {
    const map = useMap();
    useEffect(() => {
        const conteneur = map.getContainer();
        let image = null;
        const ro = new ResizeObserver(() => {
            if (image) cancelAnimationFrame(image);
            image = requestAnimationFrame(() => map.invalidateSize());
        });
        ro.observe(conteneur);
        return () => {
            if (image) cancelAnimationFrame(image);
            ro.disconnect();
        };
    }, [map]);
    return null;
}

/** Publie zoom + centre au `moveend`, débouncé (300 ms). */
function SuiviVue({onVue, delaiMs = 300}) {
    const map = useMap();
    const rappel = useRef(onVue);
    rappel.current = onVue;
    useEffect(() => {
        let minuterie = null;
        const publier = () => {
            clearTimeout(minuterie);
            minuterie = setTimeout(() => {
                const c = map.getCenter();
                rappel.current({z: map.getZoom(), c: {lat: c.lat, lng: c.lng}});
            }, delaiMs);
        };
        map.on('moveend', publier);
        return () => {
            clearTimeout(minuterie);
            map.off('moveend', publier);
        };
    }, [map, delaiMs]);
    return null;
}

/** Expose l'instance de carte au parent (zoom des boutons, recadrage) sans qu'il importe Leaflet. */
function PontCarte({pontRef}) {
    const map = useMap();
    useEffect(() => {
        pontRef.current = {
            zoomAvant: () => map.zoomIn(),
            zoomArriere: () => map.zoomOut(),
            fermerPopups: () => map.closePopup(),
        };
        return () => {
            pontRef.current = null;
        };
    }, [map, pontRef]);
    return null;
}

/** Bloc zoom ± de la maquette (haut droite). */
function ControleZoom({pontRef}) {
    const bouton = (libelle, aria, action, haut) => (
        <button
            type="button"
            aria-label={aria}
            onClick={(e) => { e.stopPropagation(); action(); }}
            style={{width: 34, height: 34, border: 0, borderTop: haut ? 0 : '1px solid #E3E7EC', background: '#FFFFFF', font: 'inherit', fontSize: 18, fontWeight: 700, color: '#2D333B', cursor: 'pointer'}}
        >
            {libelle}
        </button>
    );
    return (
        <Surcouche style={{right: 16, top: 16, display: 'flex', flexDirection: 'column', border: '1px solid #D5DAE1', borderRadius: 12, overflow: 'hidden', background: '#FFFFFF', boxShadow: '0 1px 2px rgba(16,46,105,.06), 0 1px 3px rgba(16,46,105,.05)'}}>
            {bouton('+', 'Zoom avant', () => pontRef.current && pontRef.current.zoomAvant(), true)}
            {bouton('−', 'Zoom arrière', () => pontRef.current && pontRef.current.zoomArriere(), false)}
        </Surcouche>
    );
}

/** R10 : attribution en bas à droite (+ IGN quand le fond aérien est actif). */
function Attribution({fond}) {
    return (
        <Surcouche style={{right: 12, bottom: 8, fontSize: 10, color: '#6B7480', pointerEvents: 'none', textShadow: '0 0 3px #FFFFFF, 0 0 3px #FFFFFF'}}>
            © Leaflet · OpenStreetMap{fond === 'photo' ? ` · ${ATTRIBUTION_IGN}` : ''}
        </Surcouche>
    );
}

/** Élément posé sur la carte : n'envoie ni clic, ni molette, ni glisser à Leaflet. */
export function Surcouche({style, children, ...reste}) {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return;
        L.DomEvent.disableClickPropagation(ref.current);
        L.DomEvent.disableScrollPropagation(ref.current);
    }, []);
    return <div ref={ref} style={{position: 'absolute', zIndex: 1000, ...style}} {...reste}>{children}</div>;
}

/**
 * Popup Leaflet ancrée sur un point (B1). `onClose` est appelé à la fermeture par la croix ; la page décide quoi en faire.
 */
export function PopupPav({position, onClose, children}) {
    // autoPan seulement à l'ouverture : on le coupe 400 ms après l'ouverture (après le rendu du contenu et son premier update), pour que l'arrivée de la photo (changement de taille
    // -> popup.update() de Leaflet) ne recadre plus la carte.
    const popupRef = useRef(null);
    const surOuverture = () => {
        window.setTimeout(() => { if (popupRef.current) popupRef.current.options.autoPan = false; }, 400);
    };
    return (
        <Popup
            ref={popupRef}
            position={[position.lat, position.lng]}
            className="carte-popup"
            maxWidth={420}
            minWidth={420}
            offset={[0, -14]}
            autoPan
            keepInView={false}
            closeOnClick={false}
            autoClose={false}
            eventHandlers={{remove: onClose, add: surOuverture}}
        >
            {children}
        </Popup>
    );
}

/**
 * Carte de l'écran Carte.
 * @param {{lat, lng}[]} param.cadre   points servant au cadrage initial quand le permalien n'a ni `z` ni `c`
 * @param {object} param.vueInitiale   {z, c:{lat,lng}} lus dans le permalien (ou null)
 * @param {function} param.onVue       ({z, c}) au moveend, débouncé
 */
export default function CarteLeaflet({
    cadre, vueInitiale, onVue, pavs, etats, style, acte, fond, limites, parts, circuits, onCircuit, selectedId, onSelect, onMesure, pontRef, children,
}) {
    const avecVue = vueInitiale && vueInitiale.z != null && vueInitiale.c;
    const optionsInitiales = useRef(null);
    if (optionsInitiales.current === null) {
        if (avecVue) {
            optionsInitiales.current = {center: [vueInitiale.c.lat, vueInitiale.c.lng], zoom: vueInitiale.z};
        } else if (cadre && cadre.length > 0) {
            optionsInitiales.current = {bounds: L.latLngBounds(cadre.map((p) => [p.lat, p.lng])), boundsOptions: {padding: [30, 30], maxZoom: 16}};
        } else {
            optionsInitiales.current = {center: [47.2184, -1.5536], zoom: 11}; // Nantes, par défaut
        }
    }
    const surClicVide = (latlng, maj) => {
        const f = circuits ? trouverCircuit(circuits, latlng.lat, latlng.lng) : null;
        if (f) onCircuit(f.properties.patrimoineId, maj);
    };
    return (
        <div className="carte-pilotage" style={{position: 'relative', flex: '1 1 auto', minWidth: 0, height: '100%'}}>
            <style>{STYLE_CARTE}</style>
            <MapContainer
                {...optionsInitiales.current}
                style={{height: '100%', width: '100%', background: '#E4E9E2'}}
                zoomControl={false}
                attributionControl={false}
                maxZoom={19}
            >
                {fond === 'photo' ? <FondAerien/> : <CustomTileLayer maxZoom={19} url={Constants.MAP_TILE_OSM}/>}
                <SuiviTaille/>
                <SuiviVue onVue={onVue}/>
                <PontCarte pontRef={pontRef}/>
                <CarteCommunesLayer limites={limites} parts={style === 'aplat' ? parts : null}/>
                {circuits && <CarteCircuitsLayer enveloppes={circuits} onCircuit={onCircuit} delegue={style !== 'aplat'}/>}
                {style !== 'aplat' && (
                    <CartePavCanvasLayer pavs={pavs} etats={etats} style={style} acte={acte} selectedId={selectedId} onSelect={onSelect} onMesure={onMesure} onVide={surClicVide}/>
                )}
                <ControleZoom pontRef={pontRef}/>
                <FullscreenControl/>
                <Attribution fond={fond}/>
                {children}
            </MapContainer>
        </div>
    );
}
